/**
 * JupyterHub Workspace Service
 * Handles workspace switching and session management
 */

import config from '../config/domains';
import jupyterhubTokenService from './jupyterhubTokenService';

const API_BASE_URL = config.API_URL;
const JUPYTER_URL = config.JUPYTER_URL;

class JupyterHubWorkspaceService {
    /**
     * Get current workspace from JupyterHub session
     * @returns {Promise<string|null>} Current workspace username or null
     */
    async getCurrentWorkspace() {
        try {
            // Check if we have a JupyterHub session cookie
            const cookies = document.cookie.split(';');
            const jupyterCookie = cookies.find(
                (cookie) => cookie.trim().startsWith('jupyterhub-session-id') || cookie.trim().startsWith('jupyterhub-hub-login')
            );

            if (!jupyterCookie) {
                return null;
            }

            // Try to get user info from JupyterHub
            const response = await fetch(`${JUPYTER_URL}/hub/api/user`, {
                credentials: 'include',
                headers: {
                    Accept: 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                return data.name;
            }

            return null;
        } catch (error) {
            console.error('Error getting current workspace:', error);
            return null;
        }
    }

    /**
     * Clear JupyterHub session cookies
     */
    clearJupyterHubSession() {
        // Clear all JupyterHub related cookies
        const cookies = document.cookie.split(';');
        const jupyterCookies = cookies.filter((cookie) => {
            const name = cookie.split('=')[0].trim();
            return (
                name.includes('jupyterhub') ||
                name.includes('jupyter') ||
                name.includes('_xsrf') ||
                name.includes('hub') ||
                name === 'username' ||
                name === 'user'
            );
        });

        jupyterCookies.forEach((cookie) => {
            const name = cookie.split('=')[0].trim();
            // Clear cookie for all possible paths and domains
            const paths = ['/', '/jupyter', '/jupyter/', '/jupyter/hub', '/jupyter/hub/', '/hub', '/hub/'];
            const domains = [window.location.hostname, `.${window.location.hostname}`, 'datahub.n2nai.io', '.datahub.n2nai.io'];

            paths.forEach((path) => {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
                domains.forEach((domain) => {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
                });
            });
        });

        // Also clear sessionStorage and localStorage items related to JupyterHub
        const storageKeys = Object.keys(sessionStorage);
        storageKeys.forEach((key) => {
            if (key.includes('jupyter') || key.includes('hub')) {
                sessionStorage.removeItem(key);
            }
        });
    }

    /**
     * Logout from current workspace
     * @returns {Promise<boolean>} Success status
     */
    async logoutCurrentWorkspace() {
        try {
            // Try to logout via API
            const response = await fetch(`${JUPYTER_URL}/hub/logout`, {
                method: 'GET',
                credentials: 'include',
                redirect: 'manual' // Don't follow redirects
            });

            // Clear session cookies regardless of response
            this.clearJupyterHubSession();

            return true;
        } catch (error) {
            console.error('Error during logout:', error);
            // Still clear cookies even if logout fails
            this.clearJupyterHubSession();
            return false;
        }
    }

    /**
     * Switch to a new workspace
     * @param {string} projectName - Target project name
     * @returns {Promise<Object>} Token data for new workspace
     */
    async switchWorkspace(projectName) {
        try {
            const userToken = localStorage.getItem('user_token');
            const targetWorkspace = `${userToken}_${projectName}`.toLowerCase();

            // Get current workspace
            const currentWorkspace = await this.getCurrentWorkspace();
            console.log('Current workspace:', currentWorkspace);
            console.log('Target workspace:', targetWorkspace);

            // If we're already in the correct workspace, just return token
            if (currentWorkspace === targetWorkspace) {
                console.log('Already in correct workspace');
                return await jupyterhubTokenService.generateToken(projectName);
            }

            // If in different workspace, logout first
            if (currentWorkspace) {
                console.log('Logging out from current workspace:', currentWorkspace);
                await this.logoutCurrentWorkspace();

                // Wait a bit for logout to complete
                await new Promise((resolve) => setTimeout(resolve, 500));
            }

            // Generate new token for target workspace
            console.log('Generating token for new workspace:', targetWorkspace);
            const tokenData = await jupyterhubTokenService.generateToken(projectName);

            return tokenData;
        } catch (error) {
            console.error('Error switching workspace:', error);
            throw error;
        }
    }

    /**
     * Get iframe URL for workspace with proper session handling
     * @param {string} projectName - Project name
     * @returns {Promise<string>} Iframe URL
     */
    async getWorkspaceIframeUrl(projectName) {
        try {
            // Switch workspace if needed
            const tokenData = await this.switchWorkspace(projectName);
            const { token, jupyterhub_username } = tokenData;

            // Build URL with token
            const baseUrl = JUPYTER_URL;
            const iframeUrl = `${baseUrl}/hub/login?token=${token}&next=${encodeURIComponent(`${baseUrl}/hub/user/${jupyterhub_username}/lab`)}`;

            return iframeUrl;
        } catch (error) {
            console.error('Error getting workspace iframe URL:', error);
            throw error;
        }
    }

    /**
     * Force logout and clear all JupyterHub sessions
     */
    async forceLogoutAll() {
        try {
            // Try to logout via API first
            try {
                await fetch(`${JUPYTER_URL}/hub/logout`, {
                    method: 'GET',
                    credentials: 'include',
                    mode: 'no-cors' // Avoid CORS issues
                });
            } catch (e) {
                // Ignore errors, continue with cleanup
            }

            // Clear all session data
            this.clearJupyterHubSession();

            // Clear any stored tokens
            const keys = Object.keys(localStorage);
            keys.forEach((key) => {
                if (key.startsWith('jupyterhub_token_') || key.includes('jupyter') || key.includes('hub')) {
                    localStorage.removeItem(key);
                }
            });

            // Clear all cookies more aggressively
            document.cookie.split(';').forEach((c) => {
                const eqPos = c.indexOf('=');
                const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
                // Clear for all paths
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/jupyter`;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/jupyter/`;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/jupyter/hub`;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/jupyter/hub/`;
                // With domain variations
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.datahub.n2nai.io`;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/jupyter;domain=.datahub.n2nai.io`;
            });

            console.log('All JupyterHub sessions cleared');
            return true;
        } catch (error) {
            console.error('Error forcing logout:', error);
            return false;
        }
    }
}

export default new JupyterHubWorkspaceService();
